import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Badge, Input } from '../../../components/ui';
import {
  DollarSign,
  Plus,
  RefreshCw,
  Loader2,
  Settings,
  ShoppingCart,
  TrendingUp,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAuth } from '../../../app/auth';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';
import {
  registrarCompraRecarga,
  getSaldoRecarga,
  getHistorialSaldos,
  getDetalleSaldo,
  configurarComision,
  getComision,
  establecerSaldoInicial,
  PROVEEDORES_RECARGA,
  type SaldoRecargaInfo,
  type SaldoHistorico,
  type SaldoDetalle,
  type CompraRecargaDetalle,
  type ComisionConfig,
} from '../services/recargasService';

// ========================
// Types
// ========================

interface VendedorItem {
  id: string;
  username: string;
  nombre: string;
  role: string;
  activo: boolean;
}

// ========================
// Helpers
// ========================

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '₡0';
  return '₡' + num.toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function today(): string {
  return fechaLocalYMD();
}

// ========================
// Component
// ========================

export function RecargasPage() {
  const { user } = useAuth();
  const isLocal = user?.role === 'LOCAL';
  const isVendedor = user?.role === 'VENDEDOR_CON_CAJA';

  // ── State ──────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Vendedor selection (for LOCAL)
  const [vendedores, setVendedores] = useState<VendedorItem[]>([]);
  const [selectedVendedor, setSelectedVendedor] = useState('');

  // Saldo del día
  const [saldo, setSaldo] = useState<SaldoRecargaInfo | null>(null);
  const [fecha, setFecha] = useState(today());

  // Compra form
  const [showCompraForm, setShowCompraForm] = useState(false);
  const [montoCompra, setMontoCompra] = useState('');
  const [proveedor, setProveedor] = useState<string>(PROVEEDORES_RECARGA[0]);
  const [submitting, setSubmitting] = useState(false);

  // Config comision (per-provider)
  const [showConfig, setShowConfig] = useState(false);
  const [comisionesConfig, setComisionesConfig] = useState<Record<string, string>>({});
  const [currentComisiones, setCurrentComisiones] = useState<Record<string, string>>({});

  // Saldo inicial (LOCAL only)
  const [showSaldoInicial, setShowSaldoInicial] = useState(false);
  const [saldoInicialValue, setSaldoInicialValue] = useState('');

  // Historial
  const [showHistorial, setShowHistorial] = useState(false);
  const [historial, setHistorial] = useState<SaldoHistorico[]>([]);
  const [detalleAbierto, setDetalleAbierto] = useState<string | null>(null);
  const [detalleData, setDetalleData] = useState<SaldoDetalle | null>(null);

  // ── Load vendedores (LOCAL) ────────────────
  useEffect(() => {
    if (isLocal) {
      fetchAPI<VendedorItem[]>('/usuarios/local/vendedores')
        .then(data => {
          const filtered = data.filter(
            (u) => u.role === 'VENDEDOR_CON_CAJA' && u.activo
          );
          setVendedores(filtered);
          if (filtered.length === 1) setSelectedVendedor(filtered[0].id);
        })
        .catch(() => {});
    }
  }, [isLocal]);

  // ── Load comision config ───────────────────
  useEffect(() => {
    if (user?.localId) {
      getComision(user.localId)
        .then((data: ComisionConfig) => {
          setCurrentComisiones(data.comisiones);
          setComisionesConfig({ ...data.comisiones });
        })
        .catch(() => {});
    }
  }, [user?.localId]);

  // ── Get comision for selected provider ─────
  const getComisionForProvider = (prov: string): string => {
    return currentComisiones[prov] || '0';
  };

  // ── Load saldo ─────────────────────────────
  const loadSaldo = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const vid = isVendedor ? undefined : selectedVendedor || undefined;
      if (isLocal && !vid) {
        setSaldo(null);
        setLoading(false);
        return;
      }
      const data = await getSaldoRecarga(vid, fecha);
      setSaldo(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar saldo');
    } finally {
      setLoading(false);
    }
  }, [isVendedor, isLocal, selectedVendedor, fecha]);

  useEffect(() => {
    loadSaldo();
  }, [loadSaldo]);

  // ── Registrar compra ───────────────────────
  const handleCompra = async () => {
    if (!montoCompra || parseFloat(montoCompra) <= 0) {
      setError('Ingrese un monto válido');
      return;
    }
    if (!proveedor) {
      setError('Seleccione un proveedor');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const result = await registrarCompraRecarga({
        vendedor_id: isLocal ? selectedVendedor : undefined,
        fecha,
        monto_compra: montoCompra,
        proveedor: proveedor,
      });

      const comMonto = parseFloat(result.compra.comision_monto);
      setSuccess(
        `✅ Compra registrada: ${formatCurrency(montoCompra)} a ${proveedor}` +
        (comMonto > 0 ? ` — Comisión: ${formatCurrency(comMonto)}` : '') +
        ` — Vale: ${formatCurrency(result.compra.vale)}`
      );
      setMontoCompra('');
      setShowCompraForm(false);
      await loadSaldo();
    } catch (err: any) {
      setError(err.message || 'Error al registrar compra');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Configurar comision (per-provider) ─────
  const handleConfigComision = async () => {
    if (!user?.localId) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await configurarComision(user.localId, comisionesConfig);
      const newComisiones: Record<string, string> = {};
      for (const [prov, vals] of Object.entries(result.comisiones)) {
        newComisiones[prov] = vals.nueva;
      }
      setCurrentComisiones(newComisiones);
      setSuccess('✅ Comisiones actualizadas por proveedor');
      setShowConfig(false);
    } catch (err: any) {
      setError(err.message || 'Error al configurar comisiones');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Establecer saldo inicial ───────────────
  const handleSaldoInicial = async () => {
    if (!selectedVendedor) {
      setError('Seleccione un vendedor');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await establecerSaldoInicial(selectedVendedor, saldoInicialValue, fecha);
      setSuccess(`✅ Saldo inicial establecido: ${formatCurrency(saldoInicialValue)}`);
      setSaldoInicialValue('');
      setShowSaldoInicial(false);
      await loadSaldo();
    } catch (err: any) {
      setError(err.message || 'Error al establecer saldo inicial');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Historial ──────────────────────────────
  const loadHistorial = async () => {
    try {
      const vid = isVendedor ? undefined : selectedVendedor || undefined;
      const data = await getHistorialSaldos(vid, 30);
      setHistorial(data);
      setShowHistorial(true);
    } catch (err: any) {
      setError(err.message || 'Error al cargar historial');
    }
  };

  const toggleDetalle = async (saldoId: string) => {
    if (detalleAbierto === saldoId) {
      setDetalleAbierto(null);
      setDetalleData(null);
      return;
    }
    try {
      const data = await getDetalleSaldo(saldoId);
      setDetalleData(data);
      setDetalleAbierto(saldoId);
    } catch (err: any) {
      setError(err.message || 'Error al cargar detalle');
    }
  };

  // Preview: comision for selected provider
  const previewComisionPct = getComisionForProvider(proveedor);

  // ── Per-provider breakdown ─────────────────
  const comprasPorProveedor = useMemo(() => {
    const result: Record<string, { totalCompras: number; totalComisiones: number; count: number; compras: CompraRecargaDetalle[] }> = {};
    for (const p of PROVEEDORES_RECARGA) {
      result[p] = { totalCompras: 0, totalComisiones: 0, count: 0, compras: [] };
    }
    if (saldo?.compras) {
      for (const c of saldo.compras) {
        const prov = c.proveedor;
        if (result[prov]) {
          result[prov].totalCompras += parseFloat(c.monto_compra) || 0;
          result[prov].totalComisiones += parseFloat(c.comision_monto) || 0;
          result[prov].count += 1;
          result[prov].compras.push(c);
        }
      }
    }
    return result;
  }, [saldo]);

  const provColors: Record<string, { bg: string; border: string; text: string; badge: string; light: string }> = {
    KOLBI: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800', badge: 'bg-green-600', light: 'bg-green-100' },
    CLARO: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', badge: 'bg-red-600', light: 'bg-red-100' },
    LIBERTY: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', badge: 'bg-blue-600', light: 'bg-blue-100' },
  };

  // ── Render ─────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-blue-600" />
            Saldo de Recargas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Saldo prepago por proveedor — Compras son <strong className="text-red-600">EGRESO</strong> en caja vendedor
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadSaldo}>
            <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
          </Button>
          {isLocal && (
            <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)}>
              <Settings className="w-4 h-4 mr-1" /> Comisiones
            </Button>
          )}
        </div>
      </div>

      {/* Mensajes */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Selector vendedor (LOCAL) */}
      {isLocal && (
        <Card className="p-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={selectedVendedor}
                onChange={(e) => setSelectedVendedor(e.target.value)}
              >
                <option value="">-- Seleccionar vendedor --</option>
                {vendedores.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nombre || v.username}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
              <Input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-40"
              />
            </div>
          </div>
        </Card>
      )}

      {/* Fecha para vendedor */}
      {isVendedor && (
        <Card className="p-4">
          <div className="flex gap-4 items-end flex-wrap">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
              <Input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="text-sm text-gray-500 flex gap-3">
              {PROVEEDORES_RECARGA.map(p => (
                <span key={p}>
                  {p}: <strong>{getComisionForProvider(p)}%</strong>
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Config comisión per-provider (LOCAL) */}
      {showConfig && isLocal && (
        <Card className="p-4 border-blue-200 bg-blue-50">
          <h3 className="font-semibold text-blue-800 mb-3">Configurar Comisiones por Proveedor</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PROVEEDORES_RECARGA.map(prov => (
              <div key={prov}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {prov} (%)
                </label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={comisionesConfig[prov] || '0'}
                  onChange={(e) => setComisionesConfig(prev => ({
                    ...prev,
                    [prov]: e.target.value,
                  }))}
                  className="w-full"
                  placeholder="6.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Actual: {currentComisiones[prov] || '0'}%
                </p>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleConfigComision}
              disabled={submitting}
              size="sm"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowConfig(false)}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {/* ========================================= */}
      {/* SALDO DEL DÍA + 3 TARJETAS PROVEEDOR     */}
      {/* ========================================= */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : saldo ? (
        <>
          {/* Resumen global */}
          <Card className="p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Resumen del día — {saldo.fecha}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500 uppercase font-medium">Saldo Inicial</div>
                <div className="text-xl font-bold text-gray-800">
                  {formatCurrency(saldo.saldo_inicial)}
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-xs text-blue-600 uppercase font-medium">Compras</div>
                <div className="text-xl font-bold text-blue-800">
                  +{formatCurrency(saldo.total_compras)}
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-xs text-green-600 uppercase font-medium">Comisiones</div>
                <div className="text-xl font-bold text-green-800">
                  +{formatCurrency(saldo.total_comisiones)}
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center border-2 border-purple-200">
                <div className="text-xs text-purple-600 uppercase font-medium">Saldo Final</div>
                <div className="text-xl font-bold text-purple-800">
                  {formatCurrency(saldo.saldo_final)}
                </div>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex gap-2 pt-3 mt-3 border-t">
              {isLocal && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSaldoInicial(true)}
                >
                  <Settings className="w-4 h-4 mr-1" /> Ajustar Saldo Inicial
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={loadHistorial}
              >
                <Eye className="w-4 h-4 mr-1" /> Historial
              </Button>
            </div>
          </Card>

          {/* 3 Tarjetas de proveedor */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PROVEEDORES_RECARGA.map(prov => {
              const colors = provColors[prov] || provColors.KOLBI;
              const data = comprasPorProveedor[prov];
              const comPct = getComisionForProvider(prov);
              return (
                <Card key={prov} className={`p-4 border-2 ${colors.border} ${colors.bg}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-lg font-bold ${colors.text}`}>{prov}</h3>
                    <span className={`text-xs text-white px-2 py-0.5 rounded-full ${colors.badge}`}>
                      Com: {comPct}%
                    </span>
                  </div>

                  {/* Totales del proveedor */}
                  <div className="space-y-2 mb-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Compras:</span>
                      <span className="font-mono font-bold text-gray-800">{formatCurrency(data.totalCompras)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Comisión:</span>
                      <span className="font-mono font-bold text-green-700">+{formatCurrency(data.totalComisiones)}</span>
                    </div>
                    <div className={`flex justify-between text-sm ${colors.light} rounded px-2 py-1`}>
                      <span className="font-medium">Transacciones:</span>
                      <span className="font-bold">{data.count}</span>
                    </div>
                  </div>

                  {/* Detalle de compras */}
                  {data.compras.length > 0 && (
                    <div className="space-y-1 mb-3 text-xs">
                      {data.compras.map((c) => (
                        <div key={c.id} className="flex justify-between bg-white rounded px-2 py-1">
                          <span>{formatCurrency(c.monto_compra)}</span>
                          <span className="text-green-600">+{formatCurrency(c.comision_monto)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Botón compra */}
                  {isVendedor && (
                    <Button
                      size="sm"
                      className="w-full mt-1"
                      onClick={() => {
                        setProveedor(prov);
                        setShowCompraForm(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Compra {prov}
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        <Card className="p-6 text-center text-gray-500">
          {isLocal && !selectedVendedor
            ? 'Seleccione un vendedor para ver su saldo de recargas'
            : 'No hay datos de saldo para esta fecha'}
        </Card>
      )}

      {/* Formulario de compra */}
      {showCompraForm && (
        <Card className="p-4 border-blue-200 bg-blue-50">
          <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Registrar Compra a Proveedor
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Proveedor</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={proveedor}
                onChange={(e) => setProveedor(e.target.value)}
              >
                {PROVEEDORES_RECARGA.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Comisión: {previewComisionPct}%
              </p>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Monto de compra (₡)</label>
              <Input
                type="number"
                min="1"
                step="1"
                value={montoCompra}
                onChange={(e) => setMontoCompra(e.target.value)}
                placeholder="10000"
              />
            </div>
          </div>
          {montoCompra && parseFloat(montoCompra) > 0 && (
            <div className="mt-3 text-sm bg-white rounded-lg p-3 border">
              <div className="flex justify-between">
                <span className="text-gray-600">Proveedor:</span>
                <span className="font-medium">{proveedor}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Monto compra:</span>
                <span className="font-medium">{formatCurrency(montoCompra)}</span>
              </div>
              <div className="flex justify-between text-green-700">
                <span>Comisión ({previewComisionPct}%):</span>
                <span className="font-medium">
                  +{formatCurrency(parseFloat(montoCompra) * parseFloat(previewComisionPct) / 100)}
                </span>
              </div>
              <hr className="my-1" />
              <div className="flex justify-between font-bold text-blue-800">
                <span>Vale (sin comisión):</span>
                <span>{formatCurrency(montoCompra)}</span>
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleCompra}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Registrar
            </Button>
            <Button variant="outline" onClick={() => setShowCompraForm(false)}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {/* Formulario saldo inicial (LOCAL) */}
      {showSaldoInicial && isLocal && (
        <Card className="p-4 border-yellow-200 bg-yellow-50">
          <h3 className="font-semibold text-yellow-800 mb-3">
            Ajustar Saldo Inicial
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            Normalmente el saldo inicial se calcula automáticamente (= saldo final del día anterior).
            Use esta opción solo para correcciones o la primera vez.
          </p>
          <div className="flex gap-4 items-end">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Saldo inicial (₡)</label>
              <Input
                type="number"
                min="0"
                step="1"
                value={saldoInicialValue}
                onChange={(e) => setSaldoInicialValue(e.target.value)}
                placeholder="0"
                className="w-40"
              />
            </div>
            <Button
              onClick={handleSaldoInicial}
              disabled={submitting}
              size="sm"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowSaldoInicial(false)}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {/* Historial */}
      {showHistorial && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Historial de Saldos</h3>
            <Button variant="outline" size="sm" onClick={() => setShowHistorial(false)}>
              Cerrar
            </Button>
          </div>
          {historial.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hay registros</p>
          ) : (
            <div className="space-y-2">
              {historial.map((s) => (
                <div key={s.id}>
                  <div
                    className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleDetalle(s.id)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-sm">{s.fecha}</span>
                      <Badge className="bg-purple-100 text-purple-800 text-xs">
                        {formatCurrency(s.saldo_final)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>Ini: {formatCurrency(s.saldo_inicial)}</span>
                      <span className="text-blue-600">+{formatCurrency(s.total_compras)}</span>
                      <span className="text-green-600">+{formatCurrency(s.total_comisiones)}</span>
                      {detalleAbierto === s.id
                        ? <ChevronUp className="w-4 h-4" />
                        : <ChevronDown className="w-4 h-4" />
                      }
                    </div>
                  </div>
                  {detalleAbierto === s.id && detalleData && (
                    <div className="ml-4 mr-4 mb-2 bg-white border rounded-lg p-3">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        Compras del día ({detalleData.compras.length}):
                      </div>
                      {detalleData.compras.length === 0 ? (
                        <p className="text-gray-400 text-xs">Sin compras registradas</p>
                      ) : (
                        <div className="space-y-1">
                          {detalleData.compras.map((c) => (
                            <div key={c.id} className="flex justify-between text-xs bg-gray-50 rounded px-2 py-1">
                              <span>
                                <Badge className="bg-blue-100 text-blue-800 text-xs mr-1">{c.proveedor}</Badge>
                                {formatCurrency(c.monto_compra)}
                              </span>
                              <span className="text-green-600">
                                Com: {formatCurrency(c.comision_monto)} ({c.comision_porcentaje}%) | Vale: {formatCurrency(c.vale)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Info box */}
      <Card className="p-4 bg-gray-50 border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Reglas de Recargas</h4>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>Las recargas <strong>NO son ventas</strong>. Son saldo prepago.</li>
          <li>Compras de recarga <strong>SÍ son EGRESO</strong> en la caja del vendedor (dinero sale).</li>
          <li>Proveedores fijos: <strong>{PROVEEDORES_RECARGA.join(', ')}</strong> (3 tarjetas).</li>
          <li>Cada proveedor tiene su propio porcentaje de comisión.</li>
          <li>Comisión = monto_compra × porcentaje del proveedor.</li>
          <li>Comisión suma al saldo prepago pero <strong>NO mueve efectivo</strong>.</li>
          <li>Saldo final = saldo inicial + compras + comisiones.</li>
          <li>LOCAL solo configura comisiones, <strong>NO compra recargas</strong>.</li>
        </ul>
      </Card>
    </div>
  );
}
